import type { AssignmentRuleConditionRow, AssignmentRuleNode } from '../types/task-assignment-rule';

/**
 * Convert a legacy flat condition list into a tree, mirroring how the
 * old engine grouped: split on `OR` connectors into AND-groups, then
 * combine groups with OR. Used as a one-shot during the data
 * migration that flips `task_assignment_rules.conditions` from
 * `AssignmentRuleConditionRow[]` to `AssignmentRuleNode`.
 *
 * The result is always a single root group:
 *  - root.op = 'all' if there are no OR connectors (single AND-group),
 *  - root.op = 'any' if there are multiple AND-groups (DNF top level).
 *
 * Lossless for any flat shape — the legacy engine evaluated flat
 * lists as DNF, and DNF is a strict subset of the new tree shape.
 *
 * Empty input returns an empty 'all' group; the editor renders this
 * as a fresh root awaiting its first condition.
 */
export function flatToTree(rows: readonly AssignmentRuleConditionRow[]): AssignmentRuleNode {
  if (rows.length === 0) {
    return { kind: 'group', op: 'all', children: [] };
  }

  // Same grouping logic as the legacy engine: first row starts a
  // group, OR rows start new groups, AND rows extend the current
  // group. The first row's `connector` is ignored (legacy convention).
  const andGroups: AssignmentRuleConditionRow[][] = [];
  let current: AssignmentRuleConditionRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    if (i === 0) {
      current.push(row);
    } else if (row.connector === 'OR') {
      andGroups.push(current);
      current = [row];
    } else {
      current.push(row);
    }
  }
  if (current.length > 0) andGroups.push(current);

  const groupNodes: AssignmentRuleNode[] = andGroups.map((group) => {
    if (group.length === 1) {
      return rowToCondition(group[0]!);
    }
    return {
      kind: 'group',
      op: 'all',
      children: group.map(rowToCondition),
    };
  });

  if (groupNodes.length === 1) {
    // Single AND-group: wrap in a root group with op 'all' so the
    // editor always has a stable root to render. The semantics are
    // identical (a single-child 'all' is equivalent to that child).
    const only = groupNodes[0]!;
    if (only.kind === 'group') return only;
    return { kind: 'group', op: 'all', children: [only] };
  }

  return { kind: 'group', op: 'any', children: groupNodes };
}

function rowToCondition(row: AssignmentRuleConditionRow): AssignmentRuleNode {
  return {
    kind: 'condition',
    field: row.field,
    operator: row.operator,
    value: row.value,
  };
}

/**
 * Walk a node tree, returning the depth of the deepest path. The root
 * group counts as depth 1; each level of nesting adds one. Used by
 * the Zod validator to enforce `ASSIGNMENT_RULE_MAX_DEPTH` and by the
 * editor to gate the "+ Group" button.
 */
export function ruleNodeDepth(node: AssignmentRuleNode): number {
  if (node.kind === 'condition') return 0;
  if (node.children.length === 0) return 1;
  return 1 + Math.max(...node.children.map(ruleNodeDepth));
}

/**
 * Walk a tree looking for any **non-root** group with zero children.
 * The root may legitimately be empty (catch-all rules, or a fresh
 * rule mid-edit), but a nested empty group is always a bug — the
 * engine returns `false` for empty groups, so a rule containing one
 * silently never matches. Used by the create/update Zod refines to
 * reject the partial-edit save state where the user added a `+ Group`
 * then deleted its seeded condition.
 *
 * Returns `true` iff at least one nested empty group exists. The
 * root's own emptiness is exempt — its constraint is enforced
 * separately (regular rules require non-empty root, catch-all
 * requires empty root).
 */
export function hasEmptyNestedGroup(node: AssignmentRuleNode): boolean {
  if (node.kind === 'condition') return false;
  return node.children.some(hasEmptyOrContainsEmpty);
}

function hasEmptyOrContainsEmpty(node: AssignmentRuleNode): boolean {
  if (node.kind === 'condition') return false;
  if (node.children.length === 0) return true;
  return node.children.some(hasEmptyOrContainsEmpty);
}

/**
 * Walk a tree looking for any leaf that's missing field, operator, or
 * value. Used by both the editor (gate the submit button) and the
 * server-side Zod refine (reject partial saves). A leaf with field
 * and operator but `value: ''` (or `value: []`) is incomplete — it
 * looks valid to the type system but evaluates to false at runtime,
 * silently producing a dead rule. The save path now rejects this
 * shape with a clear error.
 *
 * Accepts the wire `AssignmentRuleNode` shape; the editor's superset
 * `EditorNode` (allows null field/operator) is handled by the same
 * boolean coercion (`!node.field` catches null, undefined, AND '').
 */
export function hasIncompleteLeaf(node: AssignmentRuleNode): boolean {
  if (node.kind === 'condition') {
    if (!node.field) return true;
    if (!node.operator) return true;
    if (Array.isArray(node.value)) return node.value.length === 0;
    return node.value === '';
  }
  return node.children.some(hasIncompleteLeaf);
}

/**
 * Stable canonicalization for hashing — sorts object keys
 * deterministically so a re-ordered-but-equivalent rule produces the
 * same hash.
 *
 * NOTE: this helper is **currently unused** in the v1 PR. It's the
 * load-bearing primitive for the next PR (LLM-cached rule summary)
 * which uses sha256(canonicalizeRuleNode(rule)) as the invalidation
 * key on the `task_assignment_rules.summary_for_hash` column. Lives
 * here in v1 so the type and tests are pinned before the consumer
 * lands — moving it inline with the consumer would force a one-PR
 * round-trip just to add the import.
 */
export function canonicalizeRuleNode(node: AssignmentRuleNode): string {
  return JSON.stringify(node, replacer);
}

function replacer(_key: string, value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    return Object.keys(obj)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = obj[k];
        return acc;
      }, {});
  }
  return value;
}
